import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ApiError, apiGet } from "../lib/api";
import { cn } from "../lib/utils";
import { type EtatAtelier, etatAtelierSchema } from "./contract";
import { envoyerSansReponse, issueIncertaine } from "./diffusion-api";
import { BoutonOccupe, useFocusDuResultat } from "./diffusion-commun";
import {
  AGENT_FAILURES_TITLE,
  AGENT_LOADING,
  AGENT_NO_FAILURES,
  AGENT_READ_FAILED,
  AGENT_RELAUNCH,
  AGENT_RELAUNCH_FAILED,
  AGENT_RELAUNCH_INCERTAINE,
  AGENT_RELAUNCHED,
  AGENT_RELAUNCHING,
  AGENT_TITLE,
  type AgentStatus,
  agentDetailText,
  agentStatus,
  agentVersionText,
  failureText,
  pauseFileText,
  relaunchLabel,
  TASK_TYPE_LABELS,
} from "./regie-texts";
import { CARTE, TITRE_CARTE } from "./reglages/cadre";

/**
 * La carte « Agent du PC » de la regie : le programme du PC de Nicolas (`packages/atelier`) fait
 * l'inventaire du stock de clips et les envoie a Castellan. La carte lit `GET /api/diffusion/atelier`
 * (backend `atelier/routes.ts`, derriere la garde de la diffusion) et dit :
 * - s'il est la : « vu il y a 2 min » jusqu'a dix minutes, « éteint depuis 3 h » au-dela (en rouge),
 *   « jamais vu » tant qu'il ne s'est jamais manifeste ;
 * - sa version, et l'etat qu'il a annonce, avec son detail s'il en a donne un ;
 * - la pause de la file (`pause`) : la file ne rend rien a l'agent tant qu'un direct est ouvert, et un direct
 *   qui reste ouvert a tort apres une longue panne l'arreterait sans bruit. Quand `pause` n'est pas `null`, la
 *   carte dit depuis quand : « File en pause : un direct est ouvert depuis 3 h. » ;
 * - les taches en echec, chacune avec sa cause telle que l'agent l'a ecrite et un bouton « Relancer »
 *   (`POST /api/diffusion/atelier/taches/{id}/relancer`), qui la remet a faire et relit l'etat. Le bouton
 *   se lit « Relancer » ; son nom accessible nomme la tache par sa cle (« Relancer la tâche
 *   televersement:clip:42 »), pour qu'on ne confonde pas deux boutons identiques.
 * Toutes les phrases sont dans `regie-texts.ts`. Rien du serveur n'est affiche que du texte : ni la
 * reponse d'une erreur, ni le corps d'une relance en echec.
 *
 * Elle ne depend pas de Twitch : elle est montee des qu'un admin est connecte. L'etat est relu toutes
 * les 30 secondes, page ouverte, sans repasser par « Lecture... ». L'age du signe de vie se calcule a
 * l'heure de la derniere lecture reussie (`dataUpdatedAt`) : il avance donc a chaque relecture.
 */

/** Le delai entre deux relectures de l'etat de l'agent, page ouverte. */
export const AGENT_REFRESH_MS = 30_000;

const AGENT_KEY = ["diffusion", "atelier"] as const;

/** Vrai pour une erreur qui dit que la session est perdue : la relire ne servirait a rien. */
function sessionLost(error: Error | null): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

const TONE_CLASSES: Record<AgentStatus["tone"], string | undefined> = {
  ok: undefined,
  neutre: "text-muted-foreground",
  problem: "text-destructive",
};

/** Relance une tache en echec : un POST sans corps, la reponse est vide (204). Elle a le delai des ecritures. */
const relaunchRequest = (id: string): Promise<void> =>
  envoyerSansReponse("POST", `/api/diffusion/atelier/taches/${encodeURIComponent(id)}/relancer`);

/**
 * Quand l'etat de l'agent se relit. Chaque lecture compte une visite au Lab : la carte de la regie, qui est la pour
 * surveiller l'agent, le relit toujours (`RELECTURE_CARTE`) ; une ligne d'en-tete d'un autre ecran le lit une fois, et
 * ne le relit que tant que cet ecran en a besoin.
 */
export type RelectureAgent = {
  /** Relit toutes les trente secondes, page ouverte. */
  readonly regulierement: boolean;
  /** Relit au retour sur la fenetre et au retour du reseau. */
  readonly auRetour: boolean;
};

const RELECTURE_CARTE: RelectureAgent = { regulierement: true, auRetour: true };

/**
 * La lecture de l'etat de l'agent, la meme route pour la carte de la regie et pour la ligne de l'onglet « La
 * semaine » ; seule la relecture (`relecture`) differe.
 */
export function useEtatAgent(relecture: RelectureAgent = RELECTURE_CARTE) {
  return useQuery({
    queryKey: AGENT_KEY,
    queryFn: () => apiGet("/api/diffusion/atelier", etatAtelierSchema),
    // Comme l'etat de la regie : une erreur n'est pas retentee sur-le-champ, et rien ne reste en
    // memoire quand la page se demonte (la personne suivante du meme onglet ne voit rien de l'agent).
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: relecture.auRetour,
    refetchOnReconnect: relecture.auRetour,
    // L'agent s'annonce toutes les minutes : la carte se relit toute seule. Une erreur passagere
    // (un deploiement) n'arrete pas la relecture ; une session perdue (401, 403) l'arrete, la page
    // le saura a sa propre relecture.
    refetchInterval: relecture.regulierement
      ? (query) => (sessionLost(query.state.error) ? false : AGENT_REFRESH_MS)
      : false,
  });
}

/**
 * L'agent en une ligne, pour l'en-tete d'un ecran qui en depend (« La semaine » : un clip dont le fichier n'est pas
 * arrive attend le PC) : « Agent vu il y a 2 min. » ou « Agent éteint depuis 3 h. » en rouge, et la pause de la file
 * quand un direct est ouvert. Les phrases sont celles de la carte ; ni la version, ni l'etat annonce, ni les taches en
 * echec : c'est la carte de la regie qui les detaille. Une lecture en echec le dit, sans rien deviner.
 *
 * L'agent se lit **une fois**. `relire` dit que l'ecran attend l'agent (un clip attend son fichier) : l'etat se relit
 * alors toutes les trente secondes, tant que c'est vrai. Jamais au retour sur la fenetre.
 */
export function AgentResume({ relire }: { relire: boolean }) {
  const etat = useEtatAgent({ regulierement: relire, auRetour: false });
  if (etat.isPending) {
    return <p className="text-[20px] text-muted-foreground">{AGENT_LOADING}</p>;
  }
  if (etat.isError) {
    return <p className="text-[20px] text-destructive">{AGENT_READ_FAILED}</p>;
  }
  const status = agentStatus(etat.data.agent, etat.dataUpdatedAt);
  return (
    <div className="flex flex-col gap-1 text-[20px]">
      <p className={cn(TONE_CLASSES[status.tone])}>{status.text}</p>
      {etat.data.pause === null ? null : (
        <p>{pauseFileText(etat.data.pause, etat.dataUpdatedAt)}</p>
      )}
    </div>
  );
}

export function AgentPC() {
  const queryClient = useQueryClient();
  const etat = useEtatAgent();

  const relance = useMutation({
    mutationFn: relaunchRequest,
    // L'etat est relu en arriere-plan : la phrase de succes ne attend pas la fin de la relecture.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AGENT_KEY });
    },
    // Sans reponse claire (delai, coupure) la relance a pu aboutir : l'etat de l'agent est relu et dit ce qu'il en est.
    // Un refus net ne relit rien, le serveur a dit non.
    onError: (erreur) => {
      if (issueIncertaine(erreur)) {
        void queryClient.invalidateQueries({ queryKey: AGENT_KEY });
      }
    },
  });
  // Une relance reussie fait sortir la tache de la liste, et son bouton avec elle : le focus va a la phrase du retour.
  const retour = useFocusDuResultat<HTMLParagraphElement>(relance.isSuccess);

  let body: ReactNode;
  if (etat.isPending) {
    body = <p className="text-muted-foreground">{AGENT_LOADING}</p>;
  } else if (etat.isError) {
    // Les donnees d'avant, s'il y en a, ne sont pas montrees : « vu il y a 2 min » serait peut-etre faux.
    body = <p className="text-destructive">{AGENT_READ_FAILED}</p>;
  } else {
    body = (
      <>
        <Etat etat={etat.data} readAt={etat.dataUpdatedAt} />
        <Echecs
          echecs={etat.data.echecs}
          occupe={relance.isPending}
          onRelaunch={(id) => {
            relance.reset();
            retour.demander();
            relance.mutate(id);
          }}
        />
        {relance.isPending ? (
          <p className="text-muted-foreground" role="status">
            {AGENT_RELAUNCHING}
          </p>
        ) : relance.isError ? (
          <p className="text-destructive" role="status">
            {issueIncertaine(relance.error) ? AGENT_RELAUNCH_INCERTAINE : AGENT_RELAUNCH_FAILED}
          </p>
        ) : relance.isSuccess ? (
          <p
            ref={retour.ref}
            tabIndex={-1}
            role="status"
            className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {AGENT_RELAUNCHED}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <section aria-labelledby="agent-pc-titre">
      <Card className={CARTE}>
        <CardHeader>
          <CardTitle id="agent-pc-titre" className={TITRE_CARTE}>
            {AGENT_TITLE}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-[20px]">{body}</CardContent>
      </Card>
    </section>
  );
}

/**
 * Si l'agent est la, sa version et ce qu'il a annonce, puis la pause de la file quand un direct est ouvert.
 * `readAt` est l'heure de la lecture, en millisecondes. La pause n'a pas de role : c'est une information, la
 * file attend et rien n'est casse (les `role="status"` de la carte sont reserves au retour d'une relance).
 */
function Etat({ etat, readAt }: { etat: EtatAtelier; readAt: number }) {
  const status = agentStatus(etat.agent, readAt);
  return (
    <>
      <p className={cn(TONE_CLASSES[status.tone])}>{status.text}</p>
      {etat.agent === null ? null : (
        <>
          <p>{agentVersionText(etat.agent.version)}</p>
          <p>{agentDetailText(etat.agent.etat, etat.agent.detail)}</p>
        </>
      )}
      {etat.pause === null ? null : <p>{pauseFileText(etat.pause, readAt)}</p>}
    </>
  );
}

/** Les taches en echec, chacune avec sa cause et son bouton « Relancer ». */
function Echecs({
  echecs,
  occupe,
  onRelaunch,
}: {
  echecs: EtatAtelier["echecs"];
  occupe: boolean;
  onRelaunch: (id: string) => void;
}) {
  if (echecs.length === 0) {
    return <p className="text-muted-foreground">{AGENT_NO_FAILURES}</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-medium">{AGENT_FAILURES_TITLE}</h3>
      <ul className="flex flex-col gap-3">
        {echecs.map((tache) => (
          <li key={tache.id} className="flex flex-col items-start gap-1">
            <p>
              <strong>{TASK_TYPE_LABELS[tache.type]}</strong> <code>{tache.cle}</code>
            </p>
            <p className="text-muted-foreground">{failureText(tache)}</p>
            <BoutonOccupe
              size="sm"
              variant="outline"
              occupe={occupe}
              aria-label={relaunchLabel(tache.cle)}
              onClick={() => onRelaunch(tache.id)}
            >
              {AGENT_RELAUNCH}
            </BoutonOccupe>
          </li>
        ))}
      </ul>
    </div>
  );
}
